import React from "react";
// ── Kitchen Display System ───────────────────────────────────────────────────
import KitchenBoard from "./views/Kitchen/KitchenBoard.js";
import KitchenStations from "./views/Kitchen/KitchenStations.js";
import KitchenAnalytics from "./views/Kitchen/KitchenAnalytics.js";
import OpsKitchenBoard from "./views/Kitchen/OpsKitchenBoard.js";

import { FaWhatsapp } from "react-icons/fa";
import { TbReceiptTax } from "react-icons/tb";
import Dashboard from "./views/Dashboard.js";
import VendorActivityFeed from "./views/VendorActivityFeed.js";
import StockLedgerView from "./views/StockLedgerView.js";
import StaffSessionsView from "./views/StaffSessionsView.js";
import AuditReportsView from "./views/AuditReportsView.js";
import DataExportView from "./views/DataExportView.js";
import FiscalClassificationView from "./views/FiscalClassificationView.js";
import FiscalDocumentsView from "./views/FiscalDocumentsView.js";
import FiscalHealthView from "./views/FiscalHealthView.js";
import SeedReviewView from "./views/SeedReviewView.js";
import CatalogChangeReviewView from "./views/CatalogChangeReviewView.js";
import PosPinAdminView from "./views/PosPinAdminView.js";
import PosTerminalFleetView from "./views/PosTerminalFleetView.js";
import SupportCaseQueue from "./views/SupportCaseQueue.js";
import EntitlementGate from "./components/EntitlementGate.js";

// Phase 8 — HOC that wraps any audit-trail route component in an
// EntitlementGate. Denied users see a friendly empty state instead of the
// view's shell + a flurry of failing backend calls. Applied at route
// definition below rather than inside each view file so the view files
// remain entitlement-unaware (single-responsibility).
const withEntitlementGate = (Component, moduleKey) => (props) =>
  (
    <EntitlementGate moduleKey={moduleKey}>
      <Component {...props} />
    </EntitlementGate>
  );
/**
 * The Kitchen menu, built once and shared by the vendor sidebar and the
 * platform-staff sidebars so the two can never drift apart.
 *
 * Declared HERE, above every route array, rather than pushed on at the bottom
 * of this file the way the audit and wiki groups are. Those are reference
 * material and belong last; a kitchen board is the screen a restaurant lives
 * on all day and has to sit near the top. A `const` referenced inside an array
 * literal must already be initialised when that literal is evaluated, so the
 * factory cannot move down the file.
 *
 * `requiredPermission` is only meaningful on the staff sidebars, where
 * usePermissions().can() gates the menu. A vendor's own sidebar is chosen by
 * operationMode and carries no RBAC keys, which is why the vendor variant
 * leaves it off rather than inventing a key their account will never hold.
 */
const buildKitchenGroup = ({ staff = false } = {}) => ({
  collapse: true,
  path: "/kitchen-display",
  name: "Kitchen",
  state: "openKitchenDisplay",
  icon: "nc-icon nc-time-alarm",
  iconColor: "#f97316", // kitchen / heat orange
  ...(staff ? { requiredPermission: "kitchen:read" } : {}),
  views: [
    ...(staff
      ? [
          {
            path: "/kitchen-live",
            layout: "/admin",
            name: "Live Kitchens",
            mini: "LK",
            component: OpsKitchenBoard,
          },
        ]
      : []),
    {
      path: "/kitchen-board",
      layout: "/admin",
      name: staff ? "Kitchen Board" : "Kitchen Board",
      mini: "KB",
      component: KitchenBoard,
    },
    {
      path: "/kitchen-stations",
      layout: "/admin",
      name: "Stations & Routing",
      mini: "KS",
      component: KitchenStations,
    },
    {
      path: "/kitchen-reports",
      layout: "/admin",
      name: "Kitchen Reports",
      mini: "KR",
      component: KitchenAnalytics,
    },
  ],
});

import ListForm from "./views/Forms/ListForm";
import Menu from "./views/Menu";
import RestaurantVendorMenu from "./views/RestaurantVendorMenu";
import Restaurants from "./views/Restaurants.js";
import PromoForm from "./views/Forms/PromoForm";
import ShopCard from "./views/Forms/Listings/ShopCard";
import DeactivatedListings from "./views/Forms/Listings/DeactivatedListings";
import PaginatedOrderTable from "./views/Tables/PaginatedOrderTable";
import RestaurantOrderTable from "./views/Tables/RestaurantOrderTable";
import VendorOrderTable from "./views/Tables/VendorOrderTable";
import MobileWebOrderTable from "./views/Tables/MobileWebOrderTable";
import UserForm from "./views/Forms/UserForm.js";
import Users from "./views/Components/Users.js";
import MobileUsers from "./views/Components/MobileUsers.js";
import DeletionRequests from "./views/Components/DeletionRequests.js";
import CartScreen from "./views/Cart/CartScreen.js";
import CategoryForm from "./views/Forms/CategoryForm.js";
import CategoriesTable from "./views/Tables/CategoriesTable.js";
import RidersTable from "./views/Tables/RidersTable.js";
import DeactiveCategoriesTable from "./views/Tables/DeactiveCategoriesTable.js";
import SiUnitForm from "./views/Forms/SiUnitForm.js";
import RiderForm from "./views/Forms/RiderForm.js";
import NotificationsCenter from "./views/Notifications/NotificationsCenter.js";
import SmsStatistics from "./views/Forms/SmsStatistics.js";
import Sellers from "./views/Components/Sellers";
import ListingDetails from "./views/Forms/Listings/ListingDetails";
import SellerDetailsCard from "./views/Forms/Sellers/SellerDetailsCard";
import OrderDetailsCard from "./views/Forms/Listings/Listing/OrderDetailsCard";
import RestaurantOrderDetails from "./views/Forms/Listings/Listing/RestaurantOrderDetails";
import ShippingScreen from "./views/Shipping/ShippingScreen";
import PaymentScreen from "./views/Payment/PaymentScreen";
import PlaceOrderScreen from "./views/PlaceOrder/PlaceOrderScreen";
import PayOrderScreen from "./views/PlaceOrder/PayOrderScreen";
import ListingEditForm from "./views/Forms/ListingEditForm";
import SellerEditForm from "./views/Forms/Sellers/SellerEditForm";
import AgentPasswordResetRequestScreen from "./views/Forms/AgentPasswordResetRequestScreen";
import UserEdit from "./views/Forms/UserEdit.js";
import SiUnitTable from "./views/Tables/SiUnitTable";
import VendorOrderDetailsCard from "./views/Forms/Listings/Listing/VendorOrderDetailsCard";
import processRefund from "views/Forms/ProcessRefundForm";
import OutOfStock from "./views/Forms/Listings/OutOfStock";
import CreateRestaurantProductForm from "./views/CreateRestaurantProductForm.js";
import EditMenuProductForm from "./views/EditMenuProductForm.js";
import RestaurantVendorDashboard from "./views/RestaurantVendorDashboard.js";
import RestaurantVendorOrderDetails from "./views/Forms/Listings/Listing/RestaurantVendorOrderDetails.js";
import SellerDashboard from "./views/SellerDashboard.js";
import RestaurantProfile from "./views/Forms/Listings/Listing/RestaurantProfile.js";
import RetailerWholesalerVendorMenu from "./views/RetailerWholesalerVendorMenu.js";
import CreateRetailerWholesalerProductForm from "./views/CreateRetailerWholesalerProductForm.js";
import RetailerWholesalerOrderTable from "./views/Tables/RetailerWholesalerOrderTable.js";
import RetailerWholesalerOrderDetails from "./views/Forms/Listings/Listing/RetailerWholesalerOrderDetails.js";
import RetailerWholesalerProfile from "./views/Forms/Listings/Listing/RetailerWholesalerProfile.js";
import EditCatalogProductForm from "./views/EditCatalogProductForm.js";
import Login from "./views/Pages/Login.js";
import RetailerRidersTable from "./views/Tables/RetailerRidersTable.js";
import VendorRiders from "./views/Vendor/VendorRiders.js";
import VendorRiderTrackingMap from "./views/Vendor/VendorRiderTrackingMap.js";
import SuperAdminOrderDetails from "./views/Forms/Listings/Listing/SuperAdminOrderDetails.js";
import SuperAdminRestaurantOrderDetails from "./views/Forms/Listings/Listing/SuperAdminRestaurantOrderDetails.js";
import RetailersWholesalers from "./views/RetailersWholesalers.js";
import RetailerWholesalerAccount from "./views/RetailerWholesalerAccount.js";
import PackageDeliveryManagement from "./views/Forms/Listings/Listing/PackageDeliveryManagement.js";
import AdvertManager from "./views/Adverts/AdvertManager.js";
import OmnichannelInbox from "./views/OmnichannelInbox.js";
import ConversationAnalytics from "./views/ConversationAnalytics.js";
import Campaigns from "./views/Campaigns.js";
import Healthcares from "./views/Healthcares.js";
import HealthcareVendorDashboard from "./views/HealthcareVendorDashboard.js";
import HealthcareVendorMenu from "./views/HealthcareVendorMenu.js";
import HealthcareAccount from "./views/HealthcareAccount.js";
import CreateHealthcareProductForm from "./views/CreateHealthcareProductForm.js";
import EditHealthcareProductForm from "./views/EditHealthcareProductForm.js";
import HealthcareOrderTable from "./views/Tables/HealthcareOrderTable.js";
import HealthcareVendorOrderDetails from "./views/Forms/Listings/Listing/HealthcareVendorOrderDetails.js";
import BrandManagement from "./views/Publications/BrandManagement.js";
import BrandQuotesAdmin from "./views/Publications/BrandQuotesAdmin.js";
import FeaturedImageUpload from "./views/Publications/FeaturedImageUpload.js";
import OutletManagementBrand from "./views/Publications/OutletManagementBrand.js";
import BrandProductManagement from "./views/Publications/BrandProductManagement.js";
import CreateBrandProductForm from "./views/Publications/CreateBrandProductForm.js";
import EditBrandProductForm from "./views/Publications/EditBrandProductForm.js";
import BrandOrderTable from "./views/Tables/BrandOrderTable.js";
import BrandOrderDetails from "./views/Forms/Listings/Listing/BrandOrderDetails.js";
import BrandVendorOrderDetails from "./views/Forms/Listings/Listing/BrandVendorOrderDetails.js";
import KnowledgeBaseWiki from "./views/Wiki/KnowledgeBaseWiki.js";
import BrandOwnerSettings from "./views/Publications/BrandOwnerSettings.js";
import FreightOrders from "./views/DeliveryFee/FreightOrders.jsx";
// 3PL Partner Management
import PartnerList from "./views/ThreePL/PartnerList.js";
import CreatePartner from "./views/ThreePL/CreatePartner.js";
import PartnerDetails from "./views/ThreePL/PartnerDetails.js";
import PartnerDashboard from "./views/ThreePL/PartnerDashboard.js";
import PartnerRiders from "./views/ThreePL/PartnerRiders.js";
import PartnerOrders from "./views/ThreePL/PartnerOrders.js";
import PartnerEarnings from "./views/ThreePL/PartnerEarnings.js";
import PartnerProfile from "./views/ThreePL/PartnerProfile.js";
import RiderTrackingMap from "./views/ThreePL/RiderTrackingMap.js";
import StaffList from "./views/ThreePL/StaffList.js";
import PartnerCashControl from "./views/ThreePL/PartnerCashControl.js";
import PartnerPayouts from "./views/ThreePL/PartnerPayouts.js";
import PartnerIncentives from "./views/ThreePL/PartnerIncentives.js";
import PartnerAutoAssign from "./views/ThreePL/PartnerAutoAssign.js";
import PartnerSecurity from "./views/ThreePL/PartnerSecurity.js";
import PartnerServiceAreas from "./views/ThreePL/PartnerServiceAreas.js";
import PartnerHubs from "./views/ThreePL/PartnerHubs.js";
import CreateStaff from "./views/ThreePL/CreateStaff.js";
import FeeTransparency from "./views/DeliveryFee/FeeTransparency.jsx";
import BlogManager from "./views/BlogManager.js";
import WalletDashboard from "./views/WalletDashboard.js";
import VendorWallet from "./views/VendorWallet.js";
import VendorMediaUsage from "./views/VendorMediaUsage.js";
import TicketingConsole from "./views/TicketingConsole.js";
import TicketingDashboard from "./views/TicketingDashboard.js";
import TicketingScanner from "./views/TicketingScanner.js";
import TicketOrderDetails from "./views/Ticketing/TicketOrderDetails.js";
import TicketOrdersRedirect from "./views/Ticketing/TicketOrdersRedirect.js";
import OrganizerTeam from "./views/OrganizerTeam.js";
import OrganizerAlerts from "./views/OrganizerAlerts.js";
import OrganizerActivityLog from "./views/OrganizerActivityLog.js";
import CountryManagement from "./views/Rbac/CountryManagement.js";
import PaymentProviderLogView from "./views/PaymentProviderLogView.js";
// The SETTINGS group (31 Oct 2026): one place for every platform setting, each
// section a page built from views/Settings/settingsCatalogue.js. The old
// settings routes below stay as hidden redirects so bookmarks still land.
import { settingsPageFor } from "./views/Settings/SettingsSectionPage";
import {
  SETTINGS_BASE,
  SETTINGS_MENU_SECTIONS,
  sectionByKey,
  sectionRoutePath,
  sectionUrl,
  sectionsInMenu,
} from "./views/Settings/settingsCatalogue";
import {
  settingsRedirectTo,
  settingsRedirectKeepingQuery,
} from "./views/Settings/SettingsRedirect";
import { vendorSettingsGroup } from "./views/Settings/vendorSettingsGroup";

/**
 * The sidebar entry for one Settings section page, wherever it is listed:
 * the SETTINGS group, or another group such as Tax for Commission & tax.
 */
const settingsSectionRoute = (section) => ({
  path: sectionRoutePath(section),
  layout: "/admin",
  name: section.label,
  ...(section.sidebarIcon
    ? { icon: section.sidebarIcon }
    : {
        mini: section.label
          .split(/[\s&]+/)
          .filter(Boolean)
          .slice(0, 2)
          .map((w) => w[0].toUpperCase())
          .join(""),
      }),
  component: settingsPageFor(section.key),
  requiredPermission: section.requiredPermission,
});

const publicRoutes = [
  // {
  //   path: "/auth/login-page",
  //   layout: "/admin",
  //   name: "Login Page",
  //   component: Login,
  // },
  {
    path: "/resetPassword",
    layout: "/public",
    name: "Reset Password",
    component: AgentPasswordResetRequestScreen,
  },
];
// Auth route - Only show Login, hide Dashboard since user isn't authenticated
const authRoute = [
  {
    path: "/dashboard",
    layout: "/admin",
    name: "Dashboard",
    icon: "nc-icon nc-chart-pie-35",
    iconColor: "#4f9cf9", // analytics blue
    component: Dashboard,
    hidden: true, // Hide from sidebar on login page
  },
  {
    path: "/login-page",
    layout: "/auth",
    name: "Login",
    icon: "nc-icon nc-key-25",
    component: Login,
  },
];

// `requiredPermission` is optional and defaults to unset, so every vendor type
// that already called this keeps the inbox ungated. 3PL passes one, because a
// staff member with no order access has no reason to be reading customer
// conversations about those orders.
//
// The inbox holds every customer conversation of the business, so it is head
// office's (4 Oct 2026): `wholeBusinessOnly` drops it for a team member
// limited to some branches, as the server refuses them the inbox rooms.
const vendorInboxGroup = (state = "openVendorInbox", requiredPermission) => ({
  collapse: true,
  path: "/omnichannel",
  name: "Whatsapp",
  state,
  wholeBusinessOnly: true,
  ...(requiredPermission ? { requiredPermission } : {}),
  icon: "nc-icon nc-chat-33",
  iconComponent: <FaWhatsapp />,
  iconColor: "#25d366", // WhatsApp green
  views: [
    {
      path: "/omnichannel-inbox",
      layout: "/admin",
      name: "Customer Chats",
      mini: "CC",
      component: OmnichannelInbox,
      // Declared once in the shared group, so every vendor type and the 3PL
      // partners all get it without six copies to keep in step.
      liveBadge: "inboxUnread",
    },
    {
      path: "/conversation-analytics",
      layout: "/admin",
      name: "Chat Analytics",
      mini: "CA",
      component: ConversationAnalytics,
    },
    {
      path: "/whatsapp-campaigns",
      layout: "/admin",
      name: "Campaigns",
      mini: "CM",
      component: Campaigns,
    },
  ],
});

// Super user routes
const superUserRoutes = [
  {
    path: "/dashboard",
    layout: "/admin",
    name: "Dashboard",
    icon: "nc-icon nc-chart-pie-35",
    iconColor: "#4f9cf9", // analytics blue
    component: Dashboard,
    requiredPermission: "analytics:read",
  },

  {
    collapse: true,
    path: "/shop",
    name: "SHOP",
    state: "openShop",
    icon: "nc-icon nc-bag",
    iconColor: "#ffb547", // shop / catalog amber
    requiredPermission: "shop:read",
    views: [
      {
        path: "/shopCards",
        layout: "/admin",
        name: "E-Shop",
        mini: "ES",
        component: ShopCard,
      },
      {
        path: "/listing-details/:id",
        name: "Listing Details",
        icon: "nc-icon nc-single-copy-04",
        component: ListingDetails,
        layout: "/admin",
        hidden: true,
      },
      {
        path: "/shopEdit/:id",
        name: "Edit Listing",
        component: ListingEditForm,
        layout: "/admin",
        hidden: true,
      },
      {
        path: "/shipping",
        name: "Shipping Address",
        component: ShippingScreen,
        layout: "/admin",
        hidden: true,
      },
      {
        path: "/payment",
        name: "Payment",
        component: PaymentScreen,
        layout: "/admin",
        hidden: true,
      },
      {
        path: "/placeorder",
        name: "Place Order",
        component: PlaceOrderScreen,
        layout: "/admin",
        hidden: true,
      },
      {
        path: "/order/:id",
        name: "Pay Order",
        component: PayOrderScreen,
        layout: "/admin",
        hidden: true,
      },
      {
        path: "/deactivatedListings",
        layout: "/admin",
        name: "Deactivated Listings",
        mini: "DL",
        component: DeactivatedListings,
      },
      {
        path: "/outOfStockListings",
        layout: "/admin",
        name: "Out Of Stock",
        mini: "OS",
        component: OutOfStock,
      },
      {
        hidden: true, // moved to SETTINGS (31 Oct 2026); kept as a redirect
        path: "/cod-policy",
        layout: "/admin",
        name: "COD Limit",
        mini: "CL",
        component: settingsRedirectTo(`${SETTINGS_BASE}/orders?tab=cod-limit`),
      },
      {
        hidden: true, // moved to SETTINGS (31 Oct 2026); kept as a redirect
        path: "/product-artwork-template",
        layout: "/admin",
        name: "Artwork Templates",
        mini: "AT",
        component: settingsRedirectTo(`${SETTINGS_BASE}/catalogue?tab=artwork`),
      },
    ],
  },
  {
    collapse: true,
    path: "/restaurant",
    name: "RESTAURANT",
    state: "e-Kitchen",
    icon: "nc-icon nc-satisfied",
    iconColor: "#ff8a3d", // food / restaurant orange
    requiredPermission: "restaurants:read",
    views: [
      {
        path: "/restaurants",
        layout: "/admin",
        name: "Restaurants",
        mini: "Rt",
        component: Restaurants,
      },
      // {
      //   path: "/menu",
      //   layout: "/admin",
      //   name: "Menu",
      //   mini: "Mn",
      //   component: Menu,
      // },
      {
        path: "/menu-create",
        layout: "/admin",
        // name: "Menu Product Form",
        mini: "Mc",
        component: CreateRestaurantProductForm,
        hidden: true,
      },
      {
        path: "/edit-menu-product/:productId",
        layout: "/admin",
        // name: "Menu Product Form",
        mini: "EM",
        component: EditMenuProductForm,
        hidden: true,
      },
    ],
  },
  buildKitchenGroup({ staff: true }),
  {
    collapse: true,
    path: "/rw",
    name: "R & W",
    state: "e-traders",
    icon: "nc-icon nc-bank",
    iconColor: "#6fae5e", // retail & wholesale green
    requiredPermission: "retail_wholesale:read",
    views: [
      {
        path: "/rw-vendors",
        layout: "/admin",
        name: "Vendors",
        mini: "RW",
        component: RetailersWholesalers,
      },
      /* Catalog QC: staff "Improve" flow reuses the SAME product-edit form
         vendors use (EditCatalogProductForm). The Admin layout only registers
         the logged-in role's route tree, so without this entry a super-admin
         hitting /admin/edit-catalog-product/:id got a blank <Switch> miss —
         the route previously existed only in retailerWholesalerRoutes. */
      {
        path: "/edit-catalog-product/:productId",
        layout: "/admin",
        mini: "CP",
        component: EditCatalogProductForm,
        hidden: true,
      },
    ],
  },
  {
    collapse: true,
    path: "/healthcare",
    name: "HEALTHCARE",
    state: "e-health",
    icon: "nc-icon nc-ambulance",
    iconColor: "#ec5f8a", // healthcare rose
    requiredPermission: "healthcare:read",
    views: [
      {
        path: "/healthcare-vendors",
        layout: "/admin",
        name: "Healthcare Vendors",
        mini: "HC",
        component: Healthcares,
      },
    ],
  },
  {
    collapse: true,
    path: "/brands",
    name: "BRANDS",
    state: "e-brands",
    icon: "nc-icon nc-tag-content",
    iconColor: "#d98ae0", // brands magenta
    requiredPermission: "brands:read",
    views: [
      {
        path: "/brands-management",
        layout: "/admin",
        name: "Manage Brands",
        mini: "MB",
        component: BrandManagement,
      },
      {
        path: "/brand-b2b-quotes",
        layout: "/admin",
        name: "Brand B2B Quotes",
        mini: "BQ",
        component: BrandOrderTable,
      },
      {
        path: "/featured-image-upload",
        layout: "/admin",
        name: "Upload Featured Images",
        mini: "UFI",
        component: FeaturedImageUpload,
      },
      {
        path: "/brand-outlet-management",
        layout: "/admin",
        name: "Outlet Management",
        mini: "OM",
        component: OutletManagementBrand,
      },
      {
        path: "/brand-product-management",
        layout: "/admin",
        name: "Product Management",
        mini: "PM",
        component: BrandProductManagement,
        hidden: true,
      },
      {
        path: "/brand-product-create",
        layout: "/admin",
        name: "Create Brand Product",
        mini: "CBP",
        component: CreateBrandProductForm,
        hidden: true,
      },
      {
        path: "/brand-product-edit/:productId",
        layout: "/admin",
        name: "Edit Brand Product",
        mini: "EBP",
        component: EditBrandProductForm,
        hidden: true,
      },
      {
        path: "/brand-orders",
        layout: "/admin",
        name: "Brand Orders",
        mini: "BO",
        component: BrandOrderTable,
      },
    ],
  },
  // NOTE: "Audit & Compliance" and "Knowledge base (Wiki)" are appended at the
  // bottom of this array dynamically below, after the feature-flag check.
  {
    collapse: true,
    hidden: true,
    path: "/forms",
    name: "Forms",
    state: "openForms",
    icon: "nc-icon nc-notes",
    views: [
      {
        path: "/list-form",
        layout: "/admin",
        name: "Create Listing Form",
        mini: "LF",
        component: ListForm,
        hidden: true,
      },
      {
        path: "/promo-form",
        layout: "/admin",
        name: "Promo Form",
        mini: "PF",
        component: PromoForm,
        hidden: true,
      },
      {
        path: "/unit-form",
        layout: "/admin",
        name: "Create Unit Form",
        mini: "UF",
        component: SiUnitForm,
        hidden: true,
      },
    ],
  },
  {
    path: "/orderDetails/:id",
    layout: "/admin",
    name: "Order Details",
    mini: "OD",
    component: OrderDetailsCard,
    hidden: true,
  },
  {
    // A ticket order's own page: reservation to gate (16 Oct 2026). The
    // generic order page redirects ticket orders here.
    path: "/ticketOrderDetails/:id",
    layout: "/admin",
    name: "Ticket Order",
    mini: "TO",
    component: TicketOrderDetails,
    hidden: true,
  },
  {
    path: "/restaurantOrderDetails/:id",
    layout: "/admin",
    name: "Restaurant Order Details",
    mini: "RO",
    component: RestaurantOrderDetails,
    hidden: true,
  },
  {
    path: "/retWholesalerOrderDetails/:id",
    layout: "/admin",
    name: "Retailer/Wholesaler Order Details",
    mini: "RWO",
    component: RetailerWholesalerOrderDetails,
    hidden: true,
  },
  {
    path: "/healthcareOrderDetails/:id",
    layout: "/admin",
    name: "Healthcare Order Details",
    mini: "HO",
    component: HealthcareVendorOrderDetails,
    hidden: true,
  },
  {
    path: "/brandOrderDetails/:id",
    layout: "/admin",
    name: "Brand Order Details",
    mini: "BOD",
    component: BrandOrderDetails,
    hidden: true,
  },
  {
    path: "/brandVendorOrderDetails/:id",
    layout: "/admin",
    name: "Brand Vendor Order Details",
    mini: "BVOD",
    component: BrandVendorOrderDetails,
    hidden: true,
  },
  {
    path: "/shopEdit/:id",
    name: "Edit Listing",
    component: ListingEditForm,
    layout: "/admin",
    hidden: true,
  },
  {
    collapse: true,
    path: "/tables",
    name: "Orders",
    state: "openTables",
    icon: "nc-icon nc-cart-simple",
    iconColor: "#7b6ef6", // orders indigo
    requiredPermission: "orders:read",
    views: [
      {
        path: "/paginatedOrdersTable",
        layout: "/admin",
        name: "Manage Orders",
        mini: "PO",
        component: PaginatedOrderTable,
        // Orders still sitting at PROCESSING. The server already pushes this
        // number over the dashboardUpdate socket frame, so the badge costs
        // nothing beyond reading a field the page was already receiving.
        liveBadge: "adminProcessingOrders",
      },
      {
        path: "/vendorOrders-mng/:orderId",
        layout: "/admin",
        name: "Manage Vendors Orders",
        mini: "SAOD",
        component: SuperAdminOrderDetails,
        hidden: true,
      },
      {
        path: "/restaurantOrders-mng/:orderId",
        layout: "/admin",
        name: "Manage Restaurant Vendors Orders",
        mini: "SAROD",
        component: SuperAdminRestaurantOrderDetails,
        hidden: true,
      },
      {
        path: "/package-delivery/:id",
        layout: "/admin",
        name: "Package Delivery",
        mini: "PD",
        component: PackageDeliveryManagement,
        hidden: true,
      },
      {
        path: "/mobileWebOrdersTable",
        layout: "/admin",
        name: "Orders History",
        mini: "MO",
        component: MobileWebOrderTable,
      },
      {
        // What MTN MoMo and Flutterwave said about each charge (15 Oct 2026).
        path: "/payment-provider-log",
        layout: "/admin",
        name: "Payment Provider Log",
        mini: "PPL",
        component: PaymentProviderLogView,
      },
    ],
  },
  {
    collapse: true,
    path: "/users",
    name: "Users",
    state: "openUsers",
    icon: "nc-icon nc-single-02",
    iconColor: "#8aa0b4", // account / people slate
    requiredPermission: "users:read",
    views: [
      {
        path: "/user-form",
        layout: "/admin",
        name: "Create User",
        mini: "CU",
        component: UserForm,
        hidden: true,
      },
      {
        path: "/users",
        layout: "/admin",
        name: "Lusuku Staff",
        mini: "LS",
        component: Users,
      },
      {
        path: "/user-edit/:userId",
        layout: "/admin",
        name: "Edit Agent",
        mini: "EA",
        component: UserEdit,
        hidden: true,
      },
      {
        path: "/mobileUsers",
        layout: "/admin",
        name: "Mobile Clients",
        mini: "MC",
        component: MobileUsers,
      },
      {
        path: "/deletion-requests",
        layout: "/admin",
        name: "Deletion Requests",
        mini: "DR",
        component: DeletionRequests,
      },
      {
        path: "/sellers",
        layout: "/admin",
        name: "Sellers",
        mini: "SL",
        component: Sellers,
        // Sidebar chip: number of sellers awaiting verification (server count,
        // fed by hooks/useSellerReviewQueue.js; rendered by Sidebar.js).
        liveBadge: "pendingSellers",
      },
      {
        hidden: true, // moved to SETTINGS (31 Oct 2026); kept as a redirect
        path: "/access-control",
        layout: "/admin",
        name: "Access Control",
        mini: "AC",
        component: settingsRedirectTo(`${SETTINGS_BASE}/access?tab=staff`),
        requiredPermission: "access_control:read",
      },
      {
        // Super-admin-only operational country assignment. It belongs beside
        // staff roles and permissions because it defines each user's data
        // access boundary.
        path: "/country-management",
        layout: "/admin",
        name: "Country Access",
        mini: "CA",
        component: CountryManagement,
        requiredPermission: "access_control:read",
      },
      {
        path: "/sellerDetail/:id",
        layout: "/admin",
        name: "Seller Details",
        mini: "SL",
        component: SellerDetailsCard,
        hidden: true,
      },
      {
        path: "/sellerEdit/:id",
        layout: "/admin",
        name: "Seller Edit",
        mini: "SE",
        component: SellerEditForm,
        hidden: true,
      },
    ],
  },

  {
    collapse: true,
    path: "/ticketing",
    name: "Ticketing",
    state: "openTicketing",
    icon: "nc-icon nc-tag-content",
    // No staff role holds a "ticketing:*" default except SUPERUSER (wildcard),
    // so this keeps the platform ticketing console out of every other staff
    // sidebar until a ticketing permission is explicitly granted as an
    // override. Without a key this group rendered for ANY staff route set
    // that included it.
    requiredPermission: "ticketing",
    views: [
      {
        path: "/ticketing/dashboard",
        layout: "/admin",
        name: "Ticket Stats",
        mini: "TS",
        component: TicketingDashboard,
      },
      {
        path: "/ticketing/console",
        layout: "/admin",
        name: "Organizer Console",
        mini: "OC",
        component: TicketingConsole,
      },
      {
        // The orders list, narrowed to ticket orders. The badge counts the
        // ones that need a person: paid with no tickets, or a refund owed.
        path: "/ticketing/orders",
        layout: "/admin",
        name: "Ticket Orders",
        mini: "TO",
        component: TicketOrdersRedirect,
        requiredPermission: "orders:read",
        liveBadge: "ticketOrdersAttention",
      },
      {
        path: "/ticketing/scanner",
        layout: "/admin",
        name: "Entrance Scanner",
        mini: "ES",
        component: TicketingScanner,
      },
      {
        path: "/ticketing/team",
        layout: "/admin",
        name: "Organizer Team",
        mini: "OT",
        component: OrganizerTeam,
      },
      {
        path: "/ticketing/activity",
        layout: "/admin",
        name: "Activity Log",
        mini: "AL",
        component: OrganizerActivityLog,
      },
      {
        hidden: true, // moved to SETTINGS (31 Oct 2026); kept as a redirect
        path: "/ticketing/providers",
        layout: "/admin",
        name: "Seat Providers",
        mini: "SP",
        component: settingsRedirectTo(
          `${SETTINGS_BASE}/ticketing?tab=providers`,
        ),
        requiredPermission: "ticketing:providers:manage",
      },
      {
        hidden: true, // moved to SETTINGS (31 Oct 2026); kept as a redirect
        path: "/ticketing/settings",
        layout: "/admin",
        name: "Ticketing (Pilot)",
        mini: "TP",
        component: settingsRedirectTo(`${SETTINGS_BASE}/ticketing?tab=pilot`),
      },
    ],
  },

  {
    collapse: true,
    path: "/cart",
    name: "Cart",
    state: "opeCart",
    icon: "nc-icon nc-cart-simple",
    iconColor: "#9b8cf8", // cart light violet
    requiredPermission: "orders:read",
    views: [
      {
        path: "/cart-page",
        layout: "/admin",
        name: "Cart Page",
        mini: "CP",
        component: CartScreen,
      },
    ],
  },
  {
    collapse: true,
    path: "/delivery",
    name: "Delivery",
    state: "opeZone",
    icon: "nc-icon nc-chart",
    iconColor: "#23c4e0", // delivery / zones cyan
    requiredPermission: "delivery:read",
    views: [
      {
        hidden: true, // moved to SETTINGS (31 Oct 2026); kept as a redirect
        path: "/delivery-fee-settings",
        layout: "/admin",
        name: "Fee Settings",
        mini: "DF",
        component: settingsRedirectTo(
          `${SETTINGS_BASE}/delivery?tab=fee-engine`,
        ),
      },
      {
        path: "/fee-transparency",
        layout: "/admin",
        name: "Fee Transparency",
        mini: "FT",
        component: FeeTransparency,
      },
      {
        hidden: true, // moved to SETTINGS (31 Oct 2026); kept as a redirect
        path: "/payment-fees",
        layout: "/admin",
        name: "Payment Fees",
        mini: "PF",
        component: settingsRedirectTo(
          `${SETTINGS_BASE}/payments?tab=rail-fees`,
        ),
      },
      {
        path: "/freight-orders",
        layout: "/admin",
        name: "Arranged Freight",
        mini: "AF",
        component: FreightOrders,
      },
    ],
  },
  {
    collapse: true,
    path: "/category",
    name: "Categorization",
    state: "opeCategory",
    icon: "nc-icon nc-bullet-list-67",
    iconColor: "#5fa8d3", // categorization sky blue
    requiredPermission: "categorization:read",
    views: [
      {
        path: "/category-table",
        layout: "/admin",
        name: "Active Categories",
        mini: "AC",
        component: CategoriesTable,
      },
      {
        path: "/deactive-category-table",
        layout: "/admin",
        name: "Deactivated Categories",
        mini: "DC",
        component: DeactiveCategoriesTable,
      },
      {
        path: "/category-create",
        layout: "/admin",
        name: "Create Category",
        mini: "CC",
        component: CategoryForm,
        hidden: true,
      },
      {
        path: "/units",
        layout: "/admin",
        name: "Active Units",
        mini: "AU",
        component: SiUnitTable,
      },
    ],
  },
  {
    collapse: true,
    path: "/riders",
    name: "Riders",
    state: "opeRiders",
    icon: "nc-icon nc-delivery-fast",
    iconColor: "#23c4e0", // delivery / riders cyan
    views: [
      {
        path: "/riders-table",
        layout: "/admin",
        name: "Active Riders",
        mini: "AR",
        component: RidersTable,
        requiredPermission: "riders:read",
      },
      {
        path: "/3pl-partners",
        layout: "/admin",
        name: "3PL Partners",
        mini: "3PL",
        component: PartnerList,
        requiredPermission: "three_pl:read",
      },
      {
        path: "/3pl-partners-create",
        layout: "/admin",
        name: "Add Partner",
        mini: "AP",
        component: CreatePartner,
        hidden: true,
        requiredPermission: "three_pl:create",
      },
      {
        path: "/3pl-partners-edit/:id",
        layout: "/admin",
        name: "Edit Partner",
        mini: "EP",
        component: CreatePartner,
        hidden: true,
        requiredPermission: "three_pl:update",
      },
      {
        path: "/3pl-partner-details/:id",
        layout: "/admin",
        name: "Partner Details",
        mini: "PD",
        component: PartnerDetails,
        hidden: true,
        requiredPermission: "three_pl:read",
      },
    ],
  },
  {
    collapse: true,
    path: "/wallet",
    name: "WALLET",
    state: "openWallet",
    icon: "nc-icon nc-money-coins",
    iconColor: "#f1b50a", // money / payments gold
    requiredPermission: "wallet:read",
    views: [
      {
        path: "/wallet-dashboard",
        layout: "/admin",
        name: "Disbursements",
        mini: "WD",
        component: WalletDashboard,
      },
      {
        path: "/vendor-wallet",
        layout: "/admin",
        name: "My Wallet",
        mini: "MW",
        component: VendorWallet,
      },
      {
        hidden: true, // moved to SETTINGS (31 Oct 2026); kept as a redirect
        path: "/media-billing",
        layout: "/admin",
        name: "Media Billing",
        mini: "MB",
        component: settingsRedirectTo(
          `${SETTINGS_BASE}/catalogue?tab=media-billing`,
        ),
      },
    ],
  },
  {
    collapse: true,
    path: "/notifications",
    name: "Notifications",
    state: "opeNotifications",
    icon: "nc-icon nc-bell-55",
    iconColor: "#f5a623", // notifications amber
    // No group-level permission: EVERY operator (incl. vendors) can reach the
    // Notifications page to manage their own device + personal alert prefs. The
    // page self-gates staff-only tabs; staff-only sub-views carry their own key.
    views: [
      {
        path: "/notifications",
        layout: "/admin",
        name: "Push Notifications",
        mini: "PN",
        component: NotificationsCenter,
      },
      {
        path: "/sms-statistics",
        layout: "/admin",
        name: "SMS Statistics",
        mini: "SS",
        component: SmsStatistics,
        requiredPermission: "notifications:read", // staff-only (vendors don't see SMS stats)
      },
    ],
  },
  {
    collapse: true,
    path: "/refund",
    name: "Refund",
    state: "opeRefund",
    icon: "nc-icon nc-money-coins",
    iconColor: "#f1b50a", // money / payments gold
    requiredPermission: "refunds:read",
    views: [
      {
        path: "/refund",
        layout: "/admin",
        name: "Refund",
        mini: "R",
        component: processRefund,
      },
    ],
  },
  // Advertisment
  {
    collapse: true,
    path: "/advertisement",
    name: "Advertisement",
    state: "openAdvertisement",
    icon: "nc-icon nc-palette",
    iconColor: "#f06595", // advertising pink
    requiredPermission: "advertising:read",
    views: [
      {
        path: "/advertisement",
        layout: "/admin",
        name: "Ad Center",
        mini: "AC",
        component: AdvertManager,
      },
    ],
  },
  {
    collapse: true,
    path: "/whatsapp",
    name: "Whatsapp",
    state: "whatsappMessaging",
    icon: "nc-icon nc-notification-70",
    iconColor: "#25d366", // WhatsApp green
    requiredPermission: "messaging:read",
    views: [
      {
        path: "/omnichannel-inbox",
        layout: "/admin",
        name: "Customer Chats",
        mini: "CC",
        component: OmnichannelInbox,
      },
      {
        path: "/conversation-analytics",
        layout: "/admin",
        name: "Conversation Analytics",
        mini: "CA",
        component: ConversationAnalytics,
      },
      {
        path: "/whatsapp-campaigns",
        layout: "/admin",
        name: "Campaigns",
        mini: "CM",
        component: Campaigns,
      },
    ],
  },
  {
    collapse: true,
    path: "/pos-operations",
    name: "POS FLEET",
    state: "openPosFleet",
    icon: "nc-icon nc-tablet-2",
    iconColor: "#008f87",
    // Terminal fleet provisioning is a high-authority ops tool. This was the
    // only visible group with NO permission key, which mattered once every
    // staff role shares this tree: key it so only system:manage (SUPERUSER,
    // or an explicit grant) sees it.
    requiredPermission: "system:manage",
    views: [
      {
        path: "/pos-terminals",
        layout: "/admin",
        name: "Terminals",
        mini: "PT",
        component: PosTerminalFleetView,
      },
      {
        // Moved here from Audit & Compliance. Resetting a cashier's terminal
        // PIN is a fleet operation, not a compliance report — the person doing
        // it is standing next to the terminal, not auditing one. Living beside
        // Terminals also means the group's tighter `system:manage` key now
        // applies to it, which suits a tool that can hand someone else's
        // cashier a working PIN. The backend gates adminSetCashierPin on the
        // caller's role independently, so this move cannot widen access.
        path: "/pos-pins",
        layout: "/admin",
        name: "Cashier PINs",
        mini: "PN",
        component: PosPinAdminView,
      },
    ],
  },
  {
    path: "/blog-manager",
    layout: "/admin",
    name: "Insights & Blog",
    icon: "nc-icon nc-single-copy-04",
    iconColor: "#3fb6c9", // insights teal
    component: BlogManager,
    requiredPermission: "content:read",
  },
];

// Supervisor routes
// LEGACY (21 Aug 2026): staff no longer use a separate supervisor tree — every
// LUSUKU STAFF role gets superUserRoutes, with visibility decided entirely by
// requiredPermission against the effective permission set (role defaults +
// per-user GRANT/DENY overrides). A split tree meant a granted feature had no
// menu to reveal. Kept exported only so nothing that still imports it breaks;
// do not add new entries here.
const supervisorRoutes = [
  // Dashboard
  {
    path: "/dashboard",
    layout: "/admin",
    name: "Dashboard",
    icon: "nc-icon nc-chart-pie-35",
    iconColor: "#4f9cf9", // analytics blue
    component: Dashboard,
    requiredPermission: "analytics:read",
  },

  buildKitchenGroup({ staff: true }),

  // Shop
  {
    collapse: true,
    path: "/shop",
    name: "SHOP",
    state: "openShop",
    icon: "nc-icon nc-bag",
    iconColor: "#ffb547", // shop / catalog amber
    requiredPermission: "shop:read",
    views: [
      {
        path: "/shopCards",
        layout: "/admin",
        name: "E-Shop",
        mini: "ES",
        component: ShopCard,
      },
      {
        path: "/shipping",
        name: "Shipping Address",
        icon: "nc-icon nc-single-copy-04",
        component: ShippingScreen,
        layout: "/admin",
        hidden: true,
      },
      {
        path: "/payment",
        name: "Payment Screen",
        component: PaymentScreen,
        layout: "/admin",
        hidden: true,
      },
      {
        path: "/placeorder",
        name: "Place Order",
        component: PlaceOrderScreen,
        layout: "/admin",
        hidden: true,
      },
      {
        path: "/order/:id",
        name: "Pay Order",
        component: PayOrderScreen,
        layout: "/admin",
        hidden: true,
      },
      {
        path: "/deactivatedListings",
        layout: "/admin",
        name: "Deactivated Listings",
        mini: "DL",
        component: DeactivatedListings,
      },
      {
        path: "/outOfStockListings",
        layout: "/admin",
        name: "Out Of Stock",
        mini: "OS",
        component: OutOfStock,
      },
    ],
  },
  // Listings
  {
    collapse: true,
    hidden: true,
    path: "/forms",
    name: "Forms",
    state: "openForms",
    icon: "nc-icon nc-notes",
    views: [
      {
        path: "/list-form",
        layout: "/admin",
        name: "Create Listing Form",
        mini: "LF",
        component: ListForm,
      },
    ],
  },
  // Orders: the STAFF order-management pages, same as the super-user sidebar.
  // A supervisor is Lusuku staff: they manage the platform's orders, they do
  // not have a vendor order book of their own. The previous "My Orders" entry
  // here was VendorOrderTable, a VENDOR page that queried
  // /weborders/vendor/<the supervisor's own user id>, which can never return
  // anything for a staff account (and 500'd). See Lusuku_Supervisor_RBAC_Audit.md.
  {
    collapse: true,
    path: "/tables",
    name: "Orders",
    state: "openTables",
    icon: "nc-icon nc-cart-simple",
    iconColor: "#7b6ef6", // orders indigo
    requiredPermission: "orders:read",
    views: [
      {
        path: "/paginatedOrdersTable",
        layout: "/admin",
        name: "Manage Orders",
        mini: "PO",
        component: PaginatedOrderTable,
        // Orders still sitting at PROCESSING. The server already pushes this
        // number over the dashboardUpdate socket frame, so the badge costs
        // nothing beyond reading a field the page was already receiving.
        liveBadge: "adminProcessingOrders",
      },
      {
        path: "/vendorOrders-mng/:orderId",
        layout: "/admin",
        name: "Manage Vendors Orders",
        mini: "SAOD",
        component: SuperAdminOrderDetails,
        hidden: true,
      },
      {
        path: "/restaurantOrders-mng/:orderId",
        layout: "/admin",
        name: "Manage Restaurant Vendors Orders",
        mini: "SAROD",
        component: SuperAdminRestaurantOrderDetails,
        hidden: true,
      },
      {
        path: "/package-delivery/:id",
        layout: "/admin",
        name: "Package Delivery",
        mini: "PD",
        component: PackageDeliveryManagement,
        hidden: true,
      },
      {
        path: "/mobileWebOrdersTable",
        layout: "/admin",
        name: "Orders History",
        mini: "MO",
        component: MobileWebOrderTable,
      },
      {
        path: "/payment-provider-log",
        layout: "/admin",
        name: "Payment Provider Log",
        mini: "PPL",
        component: PaymentProviderLogView,
      },
      /* Per-vendor-type order DETAIL pages. PaginatedOrderTable's View button
         routes by the order's vendor type (orderDetails / restaurantOrderDetails
         / retWholesalerOrderDetails / healthcareOrderDetails / brandOrderDetails).
         The super-user tree registers these as hidden top-level routes; without
         them here a supervisor's View click missed the <Switch> and bounced back
         to the dashboard. Living inside this group, they inherit orders:read. */
      {
        path: "/orderDetails/:id",
        layout: "/admin",
        name: "Order Details",
        mini: "OD",
        component: OrderDetailsCard,
        hidden: true,
      },
      {
        path: "/ticketOrderDetails/:id",
        layout: "/admin",
        name: "Ticket Order",
        mini: "TO",
        component: TicketOrderDetails,
        hidden: true,
      },
      {
        path: "/restaurantOrderDetails/:id",
        layout: "/admin",
        name: "Restaurant Order Details",
        mini: "RO",
        component: RestaurantOrderDetails,
        hidden: true,
      },
      {
        path: "/retWholesalerOrderDetails/:id",
        layout: "/admin",
        name: "Retailer/Wholesaler Order Details",
        mini: "RWO",
        component: RetailerWholesalerOrderDetails,
        hidden: true,
      },
      {
        path: "/healthcareOrderDetails/:id",
        layout: "/admin",
        name: "Healthcare Order Details",
        mini: "HO",
        component: HealthcareVendorOrderDetails,
        hidden: true,
      },
      {
        path: "/brandOrderDetails/:id",
        layout: "/admin",
        name: "Brand Order Details",
        mini: "BOD",
        component: BrandOrderDetails,
        hidden: true,
      },
      {
        // OrderDetailsCard history.replace()s a brand order here when one
        // reaches the generic card, so this must be registered wherever the
        // generic card is.
        path: "/brandVendorOrderDetails/:id",
        layout: "/admin",
        name: "Brand Vendor Order Details",
        mini: "BVOD",
        component: BrandVendorOrderDetails,
        hidden: true,
      },
      {
        path: "/listing-details/:id",
        layout: "/admin",
        name: "More Order Details",
        mini: "OD",
        component: ListingDetails,
        hidden: true,
      },
    ],
  },
  {
    collapse: true,
    path: "/cart",
    name: "Cart",
    state: "opeCart",
    icon: "nc-icon nc-cart-simple",
    iconColor: "#9b8cf8", // cart light violet
    requiredPermission: "orders:read",
    views: [
      {
        path: "/cart-page",
        layout: "/admin",
        name: "Cart Page",
        mini: "CP",
        component: CartScreen,
      },
    ],
  },
  // Users
  {
    collapse: true,
    path: "/users",
    name: "Users",
    state: "openUsers",
    icon: "nc-icon nc-app",
    iconColor: "#8aa0b4", // users / people slate
    requiredPermission: "users:read",
    views: [
      {
        path: "/user-form",
        layout: "/admin",
        name: "Create User",
        mini: "CU",
        component: UserForm,
        // Staff creation is an ADMIN+ capability server-side
        // (canCreateStaffProfile). A supervisor holds users:read only, so
        // without this key the menu showed a form whose submit could only 403.
        requiredPermission: "users:create",
      },
      {
        path: "/users",
        layout: "/admin",
        name: "Lusuku Staff",
        mini: "LS",
        component: Users,
      },
      {
        path: "/mobileUsers",
        layout: "/admin",
        name: "Mobile Clients",
        mini: "MU",
        component: MobileUsers,
      },
      {
        path: "/sellers",
        layout: "/admin",
        name: "Sellers",
        mini: "SL",
        component: Sellers,
        // Sidebar chip: number of sellers awaiting verification (server count,
        // fed by hooks/useSellerReviewQueue.js; rendered by Sidebar.js).
        liveBadge: "pendingSellers",
      },
    ],
  },
  // Riders
  {
    collapse: true,
    path: "/riders",
    name: "Riders",
    state: "opeRiders",
    icon: "nc-icon nc-delivery-fast",
    iconColor: "#23c4e0", // delivery / riders cyan
    views: [
      {
        path: "/riders-table",
        layout: "/admin",
        name: "Active Riders",
        mini: "AR",
        component: RidersTable,
        requiredPermission: "riders:read",
      },
      {
        path: "/3pl-partners",
        layout: "/admin",
        name: "3PL Partners",
        mini: "3PL",
        component: PartnerList,
        requiredPermission: "three_pl:read",
      },
      {
        path: "/3pl-partners-create",
        layout: "/admin",
        name: "Add Partner",
        mini: "AP",
        component: CreatePartner,
        hidden: true,
        requiredPermission: "three_pl:create",
      },
      {
        path: "/3pl-partner-details/:id",
        layout: "/admin",
        name: "Partner Details",
        mini: "PD",
        component: PartnerDetails,
        hidden: true,
        requiredPermission: "three_pl:read",
      },
    ],
  },
  // NOTE: "Audit & Compliance" and "Knowledge base (Wiki)" are appended at the
  // bottom of this array dynamically below, after the feature-flag check.
];

// Seller routes
// const sellerRoutes = [
//   // Dashboard
//   {
//     path: "/dashboard",
//     layout: "/admin",
//     name: "Dashboard",
//     icon: "nc-icon nc-chart-pie-35",
//     component: Dashboard,
//   },

//   // Shop
//   {
//     collapse: true,
//     path: "/shop",
//     name: "SHOP",
//     state: "openShop",
//     icon: "nc-icon nc-paper-2",
//     views: [
//       {
//         path: "/shopCards",
//         layout: "/admin",
//         name: "E-Shop",
//         mini: "ES",
//         component: ShopCard,
//       },
//       {
//         path: "/shipping",
//         name: "Shipping Address",
//         icon: "nc-icon nc-single-copy-04",
//         component: ShippingScreen,
//         layout: "/admin",
//         hidden: true,
//       },
//       {
//         path: "/payment",
//         name: "Payment Screen",
//         component: PaymentScreen,
//         layout: "/admin",
//         hidden: true,
//       },
//       {
//         path: "/placeorder",
//         name: "Place Order",
//         component: PlaceOrderScreen,
//         layout: "/admin",
//         hidden: true,
//       },
//       {
//         path: "/order/:id",
//         name: "Pay Order",
//         component: PayOrderScreen,
//         layout: "/admin",
//         hidden: true,
//       },
//       {
//         path: "/deactivatedListings",
//         layout: "/admin",
//         name: "Deactivated Listings",
//         mini: "DL",
//         component: DeactivatedListings,
//       },
//       {
//         path: "/outOfStockListings",
//         layout: "/admin",
//         name: "Out Of Stock",
//         mini: "OS",
//         component: OutOfStock,
//       },
//     ],
//   },
//   // Listings
//   {
//     collapse: true,
//     path: "/forms",
//     name: "Forms",
//     state: "openForms",
//     icon: "nc-icon nc-notes",
//     views: [
//       {
//         path: "/list-form",
//         layout: "/admin",
//         name: "Create Listing Form",
//         mini: "LF",
//         component: ListForm,
//       },
//     ],
//   },
//   // Orders
//   {
//     collapse: true,
//     path: "/tables",
//     name: "Orders",
//     state: "openTables",
//     icon: "nc-icon nc-paper-2",
//     views: [
//       {
//         path: "/vendorOrdersTable",
//         layout: "/admin",
//         name: "My Orders",
//         mini: "VO",
//         component: VendorOrderTable,
//       },
//       {
//         path: "/vendorOrderDetails/:id",
//         layout: "/admin",
//         name: "My Order Details",
//         mini: "SO",
//         component: VendorOrderDetailsCard,
//         hidden: true,
//       },

//       {
//         path: "/listing-details/:id",
//         layout: "/admin",
//         name: "More Order Details",
//         mini: "OD",
//         component: ListingDetails,
//         hidden: true,
//       },
//     ],
//   },
//   {
//     collapse: true,
//     path: "/cart",
//     name: "Cart",
//     state: "opeCart",
//     icon: "nc-icon nc-cart-simple",
//     views: [
//       {
//         path: "/cart-page",
//         layout: "/admin",
//         name: "Cart Page",
//         mini: "CP",
//         component: CartScreen,
//       },
//     ],
//   },
//   // Users
//   {
//     collapse: true,
//     path: "/users",
//     name: "Users",
//     state: "openUsers",
//     icon: "nc-icon nc-app",
//     views: [
//       {
//         path: "/user-form",
//         layout: "/admin",
//         name: "Create User",
//         mini: "CU",
//         component: UserForm,
//       },
//     ],
//   },
//   // Riders
//   {
//     collapse: true,
//     path: "/riders",
//     name: "Riders",
//     state: "opeRiders",
//     icon: "nc-icon nc-delivery-fast",
//     views: [
//       {
//         path: "/riders-table",
//         layout: "/admin",
//         name: "Active Riders",
//         mini: "AR",
//         component: RidersTable,
//       },
//     ],
//   },
// ];
const restaurantVendorRoutes = [
  {
    path: "/kitchen",
    layout: "/admin",
    name: "Dashboard",
    icon: "nc-icon nc-chart-pie-35",
    iconColor: "#4f9cf9", // analytics blue
    component: RestaurantVendorDashboard,
  },
  buildKitchenGroup({ staff: false }),
  vendorInboxGroup("openRestaurantInbox"),
  {
    collapse: true,
    path: "/restaurant",
    name: "RESTAURANT",
    state: "e-Kitchen",
    icon: "nc-icon nc-alien-33",
    iconColor: "#ff8a3d", // food / restaurant orange
    views: [
      {
        path: "/restaurants",
        layout: "/admin",
        name: "Restaurants",
        mini: "Rt",
        component: Restaurants,
        vendorPermission: "catalogue:view",
      },
      {
        path: "/rest-menu",
        layout: "/admin",
        name: "Menu",
        mini: "RM",
        component: RestaurantVendorMenu,
        vendorPermission: "catalogue:view",
      },
      {
        path: "/menu-create",
        layout: "/admin",
        mini: "Mc",
        component: CreateRestaurantProductForm,
        hidden: true,
      },
      {
        path: "/edit-menu-product/:productId",
        layout: "/admin",
        mini: "EM",
        component: EditMenuProductForm,
        hidden: true,
      },
    ],
  },
  {
    collapse: true,
    path: "/orders",
    name: "Orders",
    state: "openOrders",
    vendorPermission: "orders:view",
    icon: "nc-icon nc-paper-2",
    iconColor: "#7b6ef6", // orders indigo
    views: [
      // {
      //   path: "/paginatedOrdersTable",
      //   layout: "/admin",
      //   name: "Manage Orders",
      //   mini: "PO",
      //   component: PaginatedOrderTable,
      // },
      {
        path: "/resOrdersTable",
        layout: "/admin",
        name: "Restaurant Orders",
        mini: "RO",
        component: RestaurantOrderTable,
        vendorPermission: "orders:view",
      },
      {
        path: "/restVenOrderDetails/:id",
        layout: "/admin",
        name: "Restaurant Vendor Order Details",
        mini: "RVO",
        component: RestaurantVendorOrderDetails,
        hidden: true,
      },
    ],
  },
  {
    collapse: true,
    path: "/riders",
    name: "In-House DA",
    state: "openRiders",
    icon: "nc-icon nc-delivery-fast",
    iconColor: "#23c4e0", // delivery / riders cyan
    views: [
      {
        path: "/my-riders",
        layout: "/admin",
        name: "My Riders",
        mini: "MR",
        component: VendorRiders,
      },
      {
        path: "/rider-tracking",
        layout: "/admin",
        name: "Live Tracking",
        mini: "LT",
        component: VendorRiderTrackingMap,
      },
    ],
  },
  {
    collapse: true,
    path: "/wallet",
    name: "WALLET",
    state: "openWalletRestaurant",
    // Head office only (4 Oct 2026): layouts/Admin.js drops every entry marked
    // wholeBusinessOnly for a team member limited to some branches. Money,
    // the team and the vendor's own settings belong to the whole business.
    wholeBusinessOnly: true,
    icon: "nc-icon nc-money-coins",
    iconColor: "#f1b50a", // money / payments gold
    views: [
      {
        path: "/vendor-wallet",
        layout: "/admin",
        name: "My Wallet",
        mini: "MW",
        component: VendorWallet,
      },
      {
        path: "/media-storage",
        layout: "/admin",
        name: "Media & Storage",
        mini: "MS",
        component: VendorMediaUsage,
      },
    ],
  },
];
//Retailer & Wholesaler routes
const retailerWholesalerRoutes = [
  {
    path: "/dashboard",
    layout: "/admin",
    name: "Dashboard",
    icon: "nc-icon nc-chart-pie-35",
    iconColor: "#4f9cf9", // analytics blue
    component: SellerDashboard,
  },
  vendorInboxGroup("openRetailerInbox"),
  {
    collapse: true,
    path: "/shop",
    name: "SHOP",
    state: "openShop",
    icon: "nc-icon nc-paper-2",
    iconColor: "#ffb547", // shop / catalog amber
    views: [
      {
        path: "/retailerWholesalerOrdersTable",
        layout: "/admin",
        name: "Shop Orders",
        mini: "SO",
        component: RetailerWholesalerOrderTable,
        vendorPermission: "orders:view",
      },
      {
        path: "/retWholesalerOrderDetails/:id",
        layout: "/admin",
        name: "Shop Vendor Order Details",
        mini: "RWO",
        component: RetailerWholesalerOrderDetails,
        hidden: true,
      },
      {
        path: "/catalog",
        layout: "/admin",
        name: "Catalog",
        mini: "CL",
        component: RetailerWholesalerVendorMenu,
        vendorPermission: "catalogue:view",
      },
      {
        path: "/rw-profile",
        layout: "/admin",
        name: "My Profile",
        mini: "MP",
        component: RetailerWholesalerAccount,
        wholeBusinessOnly: true,
        hidden: true,
      },
      {
        path: "/rw-product-create",
        layout: "/admin",
        mini: "Rw",
        component: CreateRetailerWholesalerProductForm,
        hidden: true,
      },
      {
        path: "/edit-catalog-product/:productId",
        layout: "/admin",
        mini: "CP",
        component: EditCatalogProductForm,
        hidden: true,
      },
    ],
  },
  {
    collapse: true,
    path: "/riders",
    name: "In-House DA",
    state: "openRiders",
    icon: "nc-icon nc-delivery-fast",
    iconColor: "#23c4e0", // delivery / riders cyan
    views: [
      {
        path: "/my-riders",
        layout: "/admin",
        name: "My Riders",
        mini: "MR",
        component: VendorRiders,
      },
      {
        path: "/rider-tracking",
        layout: "/admin",
        name: "Live Tracking",
        mini: "LT",
        component: VendorRiderTrackingMap,
      },
      {
        path: "/rw-riders",
        layout: "/admin",
        name: "Delivery Agents (Legacy)",
        mini: "Rd",
        component: RetailerRidersTable,
      },
    ],
  },
  {
    collapse: true,
    path: "/wallet",
    name: "WALLET",
    state: "openWalletRetailer",
    wholeBusinessOnly: true,
    icon: "nc-icon nc-money-coins",
    iconColor: "#f1b50a", // money / payments gold
    views: [
      {
        path: "/vendor-wallet",
        layout: "/admin",
        name: "My Wallet",
        mini: "MW",
        component: VendorWallet,
      },
      {
        path: "/media-storage",
        layout: "/admin",
        name: "Media & Storage",
        mini: "MS",
        component: VendorMediaUsage,
      },
    ],
  },
];

// Healthcare Vendor routes
const healthcareVendorRoutes = [
  {
    path: "/healthcare-dashboard",
    layout: "/admin",
    name: "Dashboard",
    icon: "nc-icon nc-chart-pie-35",
    iconColor: "#4f9cf9", // analytics blue
    component: HealthcareVendorDashboard,
  },
  vendorInboxGroup("openHealthcareInbox"),
  {
    collapse: true,
    path: "/healthcare",
    name: "HEALTHCARE",
    state: "healthcareManagement",
    icon: "nc-icon nc-ambulance",
    iconColor: "#ec5f8a", // healthcare rose
    views: [
      {
        path: "/healthcare-menu",
        layout: "/admin",
        name: "Products & Services",
        mini: "PS",
        component: HealthcareVendorMenu,
        vendorPermission: "catalogue:view",
      },
      {
        path: "/healthcare-account",
        layout: "/admin",
        name: "My Profile",
        mini: "MP",
        component: HealthcareAccount,
        wholeBusinessOnly: true,
      },
      {
        path: "/create-healthcare-product",
        layout: "/admin",
        mini: "CP",
        component: CreateHealthcareProductForm,
        hidden: true,
      },
      {
        path: "/edit-healthcare-product/:productId",
        layout: "/admin",
        mini: "EP",
        component: EditHealthcareProductForm,
        hidden: true,
      },
    ],
  },
  {
    collapse: true,
    path: "/orders",
    name: "Orders",
    state: "healthcareOrders",
    icon: "nc-icon nc-paper-2",
    iconColor: "#7b6ef6", // orders indigo
    views: [
      {
        path: "/healthcare-orders",
        layout: "/admin",
        name: "My Orders",
        mini: "HO",
        component: HealthcareOrderTable,
        vendorPermission: "orders:view",
      },
      {
        path: "/healthcareOrderDetails/:id",
        layout: "/admin",
        mini: "HOD",
        component: HealthcareVendorOrderDetails,
        hidden: true,
      },
    ],
  },
  {
    collapse: true,
    path: "/riders",
    name: "In-House DA",
    state: "openHealthcareRiders",
    icon: "nc-icon nc-delivery-fast",
    iconColor: "#23c4e0", // delivery / riders cyan
    views: [
      {
        path: "/my-riders",
        layout: "/admin",
        name: "My Riders",
        mini: "MR",
        component: VendorRiders,
      },
      {
        path: "/rider-tracking",
        layout: "/admin",
        name: "Live Tracking",
        mini: "LT",
        component: VendorRiderTrackingMap,
      },
    ],
  },
  {
    collapse: true,
    path: "/wallet",
    name: "WALLET",
    state: "openWalletHealthcare",
    wholeBusinessOnly: true,
    icon: "nc-icon nc-money-coins",
    iconColor: "#f1b50a", // money / payments gold
    views: [
      {
        path: "/vendor-wallet",
        layout: "/admin",
        name: "My Wallet",
        mini: "MW",
        component: VendorWallet,
      },
    ],
  },
];

// 3PL Partner Routes - Routes visible to 3PL partners after login
const threePLPartnerRoutes = [
  {
    path: "/3pl-partner-dashboard",
    layout: "/admin",
    name: "Dashboard",
    icon: "nc-icon nc-chart-pie-35",
    iconColor: "#4f9cf9", // analytics blue
    component: PartnerDashboard,
    // Dashboard always visible - no permission required
  },
  // Parity with the customer-selling vendor types (restaurant/retailer/
  // healthcare/brand all include this group). 3PL partners are logistics, so
  // remove this line if they shouldn't have a customer WhatsApp inbox.
  vendorInboxGroup("open3plInbox", { key: "orders", level: "view" }),
  {
    collapse: true,
    path: "/management",
    name: "Management",
    state: "openPartnerManagement",
    icon: "nc-icon nc-delivery-fast",
    iconColor: "#4f9cf9", // fleet blue
    // The group itself is keyed on riders:view. Left unkeyed it stayed in the
    // sidebar for a staff member with no rider access, opening onto nothing.
    requiredPermission: { key: "riders", level: "view" },
    views: [
      {
        path: "/partner-riders",
        layout: "/admin",
        name: "My Riders",
        mini: "MR",
        component: PartnerRiders,
        requiredPermission: { key: "riders", level: "view" },
      },
      {
        // The partner's cash-at-risk board. A rider over their held-cash limit
        // is refused new orders, so without this screen a partner's riders go
        // quiet with no visible reason.
        path: "/partner-cash",
        layout: "/admin",
        name: "Cash Control",
        mini: "CS",
        component: PartnerCashControl,
        requiredPermission: { key: "riders", level: "view" },
        // Riders blocked by held cash: capacity the partner is paying for and
        // cannot dispatch. Urgent tone, because it is money standing still.
        liveBadge: "partnerFlaggedRiders",
      },
      {
        hidden: true, // moved to SETTINGS > Auto-assign (31 Oct 2026); kept as a redirect
        path: "/partner-auto-assign",
        layout: "/admin",
        name: "Auto-Assign",
        mini: "AA",
        component: settingsRedirectTo(`${SETTINGS_BASE}/auto-assign`),
        requiredPermission: { key: "riders", level: "view" },
      },
      {
        path: "/rider-tracking",
        layout: "/admin",
        name: "Live Tracking",
        mini: "LT",
        component: RiderTrackingMap,
        requiredPermission: { key: "tracking", level: "view" },
      },
      {
        path: "/partner-orders",
        layout: "/admin",
        name: "My Orders",
        mini: "OR",
        component: PartnerOrders,
        requiredPermission: { key: "orders", level: "view" },
        // Orders Lusuku has handed this partner that still have no rider.
        liveBadge: "partnerNewAssigned",
      },
      {
        path: "/3pl-staff",
        layout: "/admin",
        name: "Staff Management",
        mini: "SM",
        component: StaffList,
        requiredPermission: { key: "staff", level: "view" },
      },
      {
        path: "/3pl-staff-create",
        layout: "/admin",
        name: "Add Staff",
        mini: "AS",
        component: CreateStaff,
        hidden: true,
        requiredPermission: { key: "staff", level: "full" },
      },
      {
        path: "/3pl-staff-edit/:id",
        layout: "/admin",
        name: "Edit Staff",
        mini: "ES",
        component: CreateStaff,
        hidden: true,
        requiredPermission: { key: "staff", level: "full" },
      },
    ],
  },
  {
    collapse: true,
    path: "/financials",
    name: "Financials",
    state: "openFinancials",
    icon: "nc-icon nc-money-coins",
    iconColor: "#f1b50a", // money / payments gold
    requiredPermission: { key: "financials", level: "view" },
    views: [
      {
        path: "/partner-earnings",
        layout: "/admin",
        name: "Earnings",
        mini: "EA",
        component: PartnerEarnings,
        requiredPermission: { key: "financials", level: "view" },
      },
      {
        // Earnings is what they have made. Payouts is what has actually been
        // paid, which is the question support was fielding by hand.
        path: "/partner-payouts",
        layout: "/admin",
        name: "Payouts",
        mini: "PO",
        component: PartnerPayouts,
        requiredPermission: { key: "financials", level: "view" },
      },
      {
        path: "/partner-incentives",
        layout: "/admin",
        name: "Incentives",
        mini: "IN",
        component: PartnerIncentives,
        requiredPermission: { key: "financials", level: "view" },
      },
    ],
  },
  {
    collapse: true,
    path: "/network",
    name: "Network",
    state: "openPartnerNetwork",
    icon: "nc-icon nc-pin-3",
    iconColor: "#2bb673", // coverage green
    requiredPermission: { key: "profile", level: "view" },
    views: [
      {
        // Coverage decides which locations this partner can be offered at all.
        path: "/partner-service-areas",
        layout: "/admin",
        name: "Service Areas",
        mini: "SA",
        component: PartnerServiceAreas,
        requiredPermission: { key: "profile", level: "view" },
      },
      {
        path: "/partner-hubs",
        layout: "/admin",
        name: "Hubs",
        mini: "HB",
        component: PartnerHubs,
        requiredPermission: { key: "profile", level: "view" },
      },
    ],
  },
];

// Brand Owner routes
const brandVendorRoutes = [
  {
    path: "/dashboard",
    layout: "/admin",
    name: "Dashboard",
    icon: "nc-icon nc-chart-pie-35",
    iconColor: "#4f9cf9", // analytics blue
    component: SellerDashboard,
  },
  vendorInboxGroup("openBrandInbox"),
  {
    collapse: true,
    path: "/shop",
    name: "SHOP",
    state: "openBrandShop",
    icon: "nc-icon nc-paper-2",
    iconColor: "#ffb547", // shop / catalog amber
    views: [
      {
        path: "/catalog",
        layout: "/admin",
        name: "Catalog",
        mini: "CL",
        component: BrandProductManagement,
        vendorPermission: "catalogue:view",
      },
      {
        path: "/brand-quotes",
        layout: "/admin",
        name: "Orders & Quotes",
        mini: "OQ",
        component: BrandOrderTable,
        vendorPermission: "orders:view",
        liveBadge: "brandOpenQuotes",
      },
      {
        path: "/brand-order-details/:id",
        layout: "/admin",
        name: "Order Details",
        mini: "OD",
        component: BrandVendorOrderDetails,
        hidden: true,
      },
      {
        path: "/brand-product-create",
        layout: "/admin",
        mini: "BC",
        component: CreateBrandProductForm,
        hidden: true,
      },
      {
        path: "/brand-product-edit/:productId",
        layout: "/admin",
        mini: "BE",
        component: EditBrandProductForm,
        hidden: true,
      },
      {
        path: "/brand-product-management",
        layout: "/admin",
        mini: "BM",
        component: BrandProductManagement,
        hidden: true,
      },
    ],
  },
  {
    collapse: true,
    path: "/brand-profile",
    name: "My Brand",
    state: "openBrandProfile",
    icon: "nc-icon nc-bag-16",
    iconColor: "#d98ae0", // brand magenta
    views: [
      {
        path: "/brand-featured-image",
        layout: "/admin",
        name: "Featured Image",
        mini: "FI",
        component: FeaturedImageUpload,
        wholeBusinessOnly: true,
      },
      {
        hidden: true, // moved to SETTINGS > Outlets (31 Oct 2026); kept as a redirect
        path: "/brand-outlets",
        layout: "/admin",
        name: "Outlets",
        mini: "OL",
        component: settingsRedirectTo(`${SETTINGS_BASE}/outlets`),
        vendorPermission: "catalogue:view",
      },
      {
        hidden: true, // moved to SETTINGS > Business profile (31 Oct 2026); kept as a redirect
        path: "/brand-settings",
        layout: "/admin",
        name: "Settings",
        mini: "ST",
        component: settingsRedirectTo(`${SETTINGS_BASE}/profile`),
        wholeBusinessOnly: true,
      },
    ],
  },
  {
    collapse: true,
    path: "/riders",
    name: "In-House DA",
    state: "openBrandRiders",
    icon: "nc-icon nc-delivery-fast",
    iconColor: "#23c4e0", // delivery / riders cyan
    views: [
      {
        path: "/my-riders",
        layout: "/admin",
        name: "My Riders",
        mini: "MR",
        component: VendorRiders,
      },
      {
        path: "/rider-tracking",
        layout: "/admin",
        name: "Live Tracking",
        mini: "LT",
        component: VendorRiderTrackingMap,
      },
    ],
  },
  {
    collapse: true,
    path: "/wallet",
    name: "WALLET",
    state: "openWalletBrand",
    wholeBusinessOnly: true,
    icon: "nc-icon nc-money-coins",
    iconColor: "#f1b50a", // money / payments gold
    views: [
      {
        path: "/vendor-wallet",
        layout: "/admin",
        name: "My Wallet",
        mini: "MW",
        component: VendorWallet,
      },
      {
        path: "/media-storage",
        layout: "/admin",
        name: "Media & Storage",
        mini: "MS",
        component: VendorMediaUsage,
      },
    ],
  },
];

// ── Audit trail (Phase 0+) ────────────────────────────────────────────────────
// Phase 0–7 feature-flag still gates compilation (REACT_APP_FEATURE_AUDIT_TRAIL).
// Phase 8 adds a second gate: per-vendor FeatureEntitlement rows, enforced
// client-side via EntitlementContext + Sidebar filtering and server-side via
// middleware/requireEntitlement.js.
//
// Each audit route carries an `entitlementKey` that the sidebar reads; when
// the caller lacks that entitlement the item is hidden, but directly-navigated
// URLs still render an EntitlementGate empty-state inside the view itself.
//
// See wiki/AUDIT_PHASE8_ENTITLEMENTS_PROGRESS.md for the rollout sequence.
const AUDIT_TRAIL_ENABLED =
  process.env.REACT_APP_FEATURE_AUDIT_TRAIL !== "false";

// Mirror of backend constants/featureModules.js — keep in sync by hand.
const ENTITLEMENT_KEYS = {
  ACTIVITY_FEED: "ACTIVITY_FEED",
  STOCK_LEDGER: "STOCK_LEDGER",
  REPORTS: "REPORTS",
  DATA_EXPORT: "DATA_EXPORT",
  VERSION_HISTORY: "VERSION_HISTORY",
};

const activityLogRoute = {
  path: "/activity-log",
  layout: "/admin",
  name: "Activity log",
  icon: "nc-icon nc-notes",
  component: withEntitlementGate(
    VendorActivityFeed,
    ENTITLEMENT_KEYS.ACTIVITY_FEED,
  ),
  entitlementKey: ENTITLEMENT_KEYS.ACTIVITY_FEED,
};

const stockLedgerRoute = {
  path: "/stock-ledger",
  layout: "/admin",
  name: "Stock ledger",
  icon: "nc-icon nc-bag",
  component: withEntitlementGate(
    StockLedgerView,
    ENTITLEMENT_KEYS.STOCK_LEDGER,
  ),
  entitlementKey: ENTITLEMENT_KEYS.STOCK_LEDGER,
};

const staffSessionsRoute = {
  path: "/staff-sessions",
  layout: "/admin",
  name: "Staff sessions",
  icon: "nc-icon nc-lock-circle-open",
  component: withEntitlementGate(
    StaffSessionsView,
    ENTITLEMENT_KEYS.ACTIVITY_FEED,
  ),
  entitlementKey: ENTITLEMENT_KEYS.ACTIVITY_FEED,
};

const reportsRoute = {
  path: "/audit-reports",
  layout: "/admin",
  name: "Reports",
  icon: "nc-icon nc-chart-bar-32",
  component: withEntitlementGate(AuditReportsView, ENTITLEMENT_KEYS.REPORTS),
  entitlementKey: ENTITLEMENT_KEYS.REPORTS,
};

const dataExportRoute = {
  path: "/data-export",
  layout: "/admin",
  name: "Data export",
  icon: "nc-icon nc-cloud-download-93",
  component: withEntitlementGate(DataExportView, ENTITLEMENT_KEYS.DATA_EXPORT),
  entitlementKey: ENTITLEMENT_KEYS.DATA_EXPORT,
};

// Phase 8 — platform-admin-only page for managing per-vendor entitlements.
// Kept separate from the other audit routes because it has no `entitlementKey`
// (it IS the entitlement-management tool) and should only appear in the super
// admin / supervisor sidebars.
const featureEntitlementsRoute = {
  hidden: true, // moved to SETTINGS > Support & programmes (31 Oct 2026); kept as a redirect
  path: "/feature-entitlements",
  layout: "/admin",
  name: "Feature entitlements",
  icon: "nc-icon nc-settings-gear-64",
  component: settingsRedirectTo(`${SETTINGS_BASE}/support?tab=entitlements`),
  // No entitlementKey — platform admins are the only allowed callers; the
  // sidebar filter only runs for non-admins, so omission keeps this visible.
};

// Platform-admin-only page for mapping the four product catalogues onto URA's
// standardised product list. No `entitlementKey` for the same reason the
// entitlements page has none: the audience is Lusuku staff, not a vendor whose
// plan might include it. A vendor cannot set a URA commodity code and the
// backend answers them 403, so the page must never appear in a vendor sidebar.
//
// Lives in the TAX group below (it was in Audit & Compliance, which put a
// hands-on mapping job behind a reference menu) and carries tax:manage: a
// wrong code misdeclares a real supply, so reading the tax menu is not enough
// to be allowed to set one.
const fiscalClassificationRoute = {
  path: "/fiscal-classification",
  layout: "/admin",
  name: "URA classification",
  icon: "nc-icon nc-tag-content",
  component: FiscalClassificationView,
  requiredPermission: "tax:manage",
};

// ── Tax (URA EFRIS) ──────────────────────────────────────────────────────────
// One menu for everything URA will look at during the UAT: the documents the
// submitter has filed, the submitter's health, the catalogue mapping and each
// vendor's EFRIS identity. The group is gated tax:read; the two screens that
// change something (classification, vendor settings) carry tax:manage of their
// own, which Admin.js's PermissionGuard applies over the group's key. The
// backend adds the `tax` domain alongside this; until a staff member holds a
// tax key the group is hidden from them and SUPERUSER sees it regardless.
//
// No `entitlementKey` on any of these: the audience is Lusuku staff. A vendor
// sees their own tax position on their profile (components/VendorTaxSettings.js)
// and never this menu.
const fiscalDocumentsRoute = {
  path: "/fiscal-documents",
  layout: "/admin",
  name: "Fiscal documents",
  icon: "nc-icon nc-paper-2",
  component: FiscalDocumentsView,
  requiredPermission: "tax:read",
};

const fiscalHealthRoute = {
  path: "/fiscal-health",
  layout: "/admin",
  name: "EFRIS health",
  icon: "nc-icon nc-check-2",
  component: FiscalHealthView,
  requiredPermission: "tax:read",
};

const vendorTaxSettingsRoute = {
  hidden: true, // moved to Commission & tax (31 Oct 2026); kept as a redirect
  path: "/vendor-tax-settings",
  layout: "/admin",
  name: "Vendor tax settings",
  icon: "nc-icon nc-badge",
  component: settingsRedirectTo(
    sectionUrl(sectionByKey("commission-tax"), "vendor-tax"),
  ),
  requiredPermission: "tax:manage",
};

// Commission & tax sat under SETTINGS until 1 Nov 2026 (Izi: move it under
// Tax). Its old address keeps working, tab and all.
const commissionTaxOldAddressRoute = {
  hidden: true,
  path: "/settings/commission-tax",
  layout: "/admin",
  name: "Commission & tax",
  component: settingsRedirectKeepingQuery(
    sectionUrl(sectionByKey("commission-tax")),
  ),
  requiredPermission: "tax:read",
};

const taxGroup = {
  collapse: true,
  path: "/tax",
  name: "Tax",
  state: "openTax",
  // A receipt with a percent sign: tax, not the music note it used to be.
  iconComponent: <TbReceiptTax />,
  iconColor: "#2dd4bf", // fiscal teal, light enough to read on the dark sidebar
  requiredPermission: "tax:read",
  views: [
    fiscalDocumentsRoute,
    fiscalHealthRoute,
    fiscalClassificationRoute,
    ...sectionsInMenu("tax").map(settingsSectionRoute),
    vendorTaxSettingsRoute,
    commissionTaxOldAddressRoute,
  ],
};

// The Tax group sits right after WALLET in the super-admin sidebar, because a
// fiscal document is raised against a commission that the wallet collected
// and the two menus are read together. A sidebar with no WALLET group (the
// supervisor's) gets it at the end of its operational menus, ahead of the
// audit group and the knowledge base that are appended further down. Same
// already-present guard as every other appended group, so a hot reload cannot
// add a second copy.
[superUserRoutes, supervisorRoutes].forEach((list) => {
  if (!Array.isArray(list) || list.some((r) => r.path === "/tax")) return;
  const afterWallet = list.findIndex((r) => r.path === "/wallet");
  if (afterWallet >= 0) list.splice(afterWallet + 1, 0, taxGroup);
  else list.push(taxGroup);
});

// SETTINGS is the LAST group in the staff sidebar (Izi, 31 Oct 2026): the
// dials sit below everything they shape, after audit and the knowledge base.
// It is appended at the very end of this file, after those. The sidebar gates
// the group on settings:read (Admin and Supervisor by default) and each
// section on its own key; what a person may CHANGE is decided per setting by
// the server (middleware/settingsAccess.js) and the page renders read-only.
const settingsGroup = {
  collapse: true,
  path: "/settings",
  name: "SETTINGS",
  state: "openSettings",
  icon: "nc-icon nc-settings-gear-64",
  iconColor: "#9fd3ff", // settings sky: light enough to read on the dark sidebar
  requiredPermission: "settings:read",
  views: SETTINGS_MENU_SECTIONS.map(settingsSectionRoute),
};
// POS PIN management now lives in the POS FLEET group beside Terminals — see
// the comment on that view. It was here, in the admin audit group, which put a
// hands-on fleet task behind a compliance menu.

// Seed feature — community vendor onboarding review queue. Only visible to
// SUPERUSER / ADMIN / SUPERVISOR because the backend endpoints are gated by
// `isSeedReviewer` (see middleware/isSeedReviewer.js). No entitlementKey.
const seedReviewRoute = {
  path: "/seed-review",
  layout: "/admin",
  name: "Seed submissions",
  icon: "nc-icon nc-sound-wave",
  component: SeedReviewView,
};

// Seed — catalog change requests. Edits proposed to already-active vendor
// catalogs via a seed submission. Ops approves/rejects; the live mutation
// is applied by a per-vendor-type sync job (see controller header).
const catalogChangeReviewRoute = {
  path: "/catalog-changes",
  layout: "/admin",
  name: "Catalog changes",
  icon: "nc-icon nc-bullet-list-67",
  component: CatalogChangeReviewView,
};

// Seed — credit config editor. Same `isSeedReviewer` backend gate.
const seedConfigRoute = {
  hidden: true, // moved to SETTINGS > Support & programmes (31 Oct 2026); kept as a redirect
  path: "/seed-config",
  layout: "/admin",
  name: "Seed credit config",
  icon: "nc-icon nc-settings",
  component: settingsRedirectTo(`${SETTINGS_BASE}/support?tab=seed-credits`),
};

// Build a single collapsible "Audit & Compliance" group per role array, so the
// sidebar is not overcrowded. Platform admins (superuser + supervisor) also see
// the `Feature entitlements` management item inside the same group.
const buildAuditGroup = ({ includeEntitlements }) => ({
  collapse: true,
  path: "/audit",
  name: "Audit & Compliance",
  state: "eAudit",
  icon: "nc-icon nc-single-copy-04",
  iconColor: "#ef6461", // caution / compliance red
  requiredPermission: "compliance:read",
  views: [
    activityLogRoute,
    stockLedgerRoute,
    staffSessionsRoute,
    reportsRoute,
    dataExportRoute,
    ...(includeEntitlements
      ? [
          featureEntitlementsRoute,
          seedReviewRoute,
          catalogChangeReviewRoute,
          seedConfigRoute,
        ]
      : []),
  ],
});

// Knowledge base — supervisor-and-above reference material for how the
// platform works end-to-end. A single sidebar entry; the individual wiki
// pages (platform overview + one per vendor/actor type) render as tabs
// inside KnowledgeBaseWiki (deep-linkable via /admin/wiki#<tab>). Rendered
// as the very last item in the sidebar so operational menus stay near the top.
const wikiGroup = {
  path: "/wiki",
  layout: "/admin",
  name: "Knowledge base",
  icon: "nc-icon nc-paper-2",
  iconColor: "#a78bfa", // reference / docs lavender
  requiredPermission: "content:read",
  component: KnowledgeBaseWiki,
};

// Support cases: the queue for every issue raised on an order, a delivery, a
// payment or a vendor. ONE view, reachable by Lusuku staff and by a vendor
// manager at the same path, because the server scopes what each caller sees
// (visibilityFilterFor in the support case service). Registered here, above the
// audit group and the knowledge base, so an operational screen sits ahead of
// the reference material in every sidebar.
//
// Deliberately carries NO `requiredPermission` and NO `entitlementKey`.
// PermissionGuard and the Sidebar treat an unkeyed route as always visible
// (see layouts/Admin.js), which is what keeps staff and vendor managers landing
// on the same screen. A permission key would hide the queue from staff who hold
// no such key, and an entitlement key would gate a vendor out of their own
// support inbox.
const supportCasesRoute = {
  path: "/support-cases",
  layout: "/admin",
  name: "Support cases",
  icon: "nc-icon nc-support-17",
  iconColor: "#2f80ed", // support blue
  component: SupportCaseQueue,
};

// Pushed onto each role array the same way the audit group and the knowledge
// base are, with the same already-present guard so a hot reload cannot add a
// second copy.
[
  superUserRoutes,
  supervisorRoutes,
  restaurantVendorRoutes,
  retailerWholesalerRoutes,
  healthcareVendorRoutes,
  brandVendorRoutes,
  threePLPartnerRoutes,
].forEach((list) => {
  if (Array.isArray(list) && !list.some((r) => r.path === "/support-cases")) {
    list.push(supportCasesRoute);
  }
});

// SETTINGS Feature.
[superUserRoutes, supervisorRoutes].forEach((list) => {
  if (Array.isArray(list) && !list.some((r) => r.path === "/settings")) {
    list.push(settingsGroup);
  }
});

// Vendor and partner SETTINGS groups (phase 2, 31 Oct 2026), in the same
// place in each sidebar as the staff one. The Account and My Team groups they
// replace are gone; /profile, /vendor-staff, /healthcare-account,
// /partner-profile, /partner-security, /brand-settings, /brand-outlets and
// /partner-auto-assign redirect into the group.
const legacyVendorSettingsRedirects = [
  { path: "/profile", to: `${SETTINGS_BASE}/profile` },
  { path: "/healthcare-account", to: `${SETTINGS_BASE}/profile` },
  { path: "/vendor-staff", to: `${SETTINGS_BASE}/team` },
  { path: "/partner-profile", to: `${SETTINGS_BASE}/company-profile` },
  { path: "/partner-security", to: `${SETTINGS_BASE}/security` },
].map(({ path, to }) => ({
  hidden: true,
  path,
  layout: "/admin",
  name: "Settings",
  mini: "ST",
  component: settingsRedirectTo(to),
}));
const partnerSettingsExtras = [
  {
    path: "/settings/company-profile",
    layout: "/admin",
    name: "Company profile",
    mini: "CP",
    component: PartnerProfile,
    requiredPermission: { key: "profile", level: "view" },
  },
  {
    // Two-factor on the PARTNER account; the endpoints resolve the signed-in
    // id against the ThreePLPartner collection, so staff keep it off their menu.
    path: "/settings/security",
    layout: "/admin",
    name: "Security",
    mini: "SE",
    component: PartnerSecurity,
    ownerOnly: true,
  },
];
[
  [restaurantVendorRoutes, { profile: RestaurantProfile, state: "openSettingsRestaurant" }],
  [retailerWholesalerRoutes, { profile: RetailerWholesalerProfile, state: "openSettingsRw" }],
  [healthcareVendorRoutes, { profile: HealthcareAccount, state: "openSettingsHealthcare" }],
  [brandVendorRoutes, { profile: BrandOwnerSettings, outlets: OutletManagementBrand, state: "openSettingsBrand" }],
  [threePLPartnerRoutes, { profile: null, autoAssign: PartnerAutoAssign, extras: partnerSettingsExtras, state: "openSettingsPartner" }],
].forEach(([list, options]) => {
  if (!Array.isArray(list) || list.some((r) => r.path === "/settings")) return;
  list.push(vendorSettingsGroup(options), ...legacyVendorSettingsRedirects);
});
if (AUDIT_TRAIL_ENABLED) {
  // Vendor-facing + 3PL role arrays get the audit group (no entitlements page).
  [
    restaurantVendorRoutes,
    retailerWholesalerRoutes,
    healthcareVendorRoutes,
    brandVendorRoutes,
    threePLPartnerRoutes,
  ].forEach((list) => {
    if (Array.isArray(list) && !list.some((r) => r.path === "/audit")) {
      list.push(buildAuditGroup({ includeEntitlements: false }));
    }
  });

  // Platform admins get the audit group WITH the entitlements management item.
  [superUserRoutes, supervisorRoutes].forEach((list) => {
    if (!Array.isArray(list)) return;
    if (!list.some((r) => r.path === "/audit")) {
      list.push(buildAuditGroup({ includeEntitlements: true }));
    }
  });
}

// Knowledge base is independent of the audit-trail feature flag — it is
// platform documentation, not a gated product module. Pinned last in each
// admin sidebar so operational menus stay near the top.
[superUserRoutes, supervisorRoutes].forEach((list) => {
  if (Array.isArray(list) && !list.some((r) => r.path === "/wiki")) {
    list.push(wikiGroup);
  }
});

// ── Permission helpers ───────────────────────────────────────────────────────
// A route may carry `requiredPermission` in two shapes:
//   1. A canonical "domain:action" string (the RBAC contract used by the
//      Lusuku-staff sidebar gating via usePermissions().can()).
//   2. A legacy { key, level } object (used by the 3PL partner/staff menu
//      filtering in layouts/Admin.js, where `level` is view|edit|create|
//      delete|manage|full).
// `normalizeRequiredPermission` collapses either form into a single
// "domain:action" string so the Sidebar's can() filter can read both. Legacy
// levels are mapped to RBAC actions: view→read, edit→update, create→create,
// delete→delete, manage/full→manage. Unkeyed routes return null (treated as
// always-visible by callers).
const LEVEL_TO_ACTION = {
  view: "read",
  edit: "update",
  create: "create",
  delete: "delete",
  manage: "manage",
  full: "manage",
};

const normalizeRequiredPermission = (requiredPermission) => {
  if (!requiredPermission) return null;
  if (typeof requiredPermission === "string") {
    return requiredPermission.includes(":")
      ? requiredPermission
      : `${requiredPermission}:read`;
  }
  if (typeof requiredPermission === "object" && requiredPermission.key) {
    const action = LEVEL_TO_ACTION[requiredPermission.level] || "read";
    return `${requiredPermission.key}:${action}`;
  }
  return null;
};

const eventOrganizerRoutes = [
  {
    collapse: true,
    path: "/ticketing",
    name: "Ticketing",
    state: "openTicketing",
    icon: "nc-icon nc-tag-content",
    views: [
      {
        path: "/ticketing/dashboard",
        layout: "/admin",
        name: "Dashboard",
        mini: "DB",
        component: TicketingDashboard,
      },
      {
        path: "/ticketing/console",
        layout: "/admin",
        name: "My Events",
        mini: "TC",
        component: TicketingConsole,
      },
      {
        path: "/ticketing/scanner",
        layout: "/admin",
        name: "Entrance Scanner",
        mini: "SC",
        component: TicketingScanner,
      },
      {
        path: "/ticketing/team",
        layout: "/admin",
        name: "My Team",
        mini: "MT",
        component: OrganizerTeam,
      },
      {
        path: "/ticketing/alerts",
        layout: "/admin",
        name: "Alert settings",
        mini: "AS",
        component: OrganizerAlerts,
      },
      {
        path: "/ticketing/activity",
        layout: "/admin",
        name: "Activity Log",
        mini: "AL",
        component: OrganizerActivityLog,
      },
    ],
  },
];

export {
  superUserRoutes,
  supervisorRoutes,
  // sellerRoutes,
  authRoute,
  publicRoutes,
  restaurantVendorRoutes,
  retailerWholesalerRoutes,
  healthcareVendorRoutes,
  threePLPartnerRoutes,
  brandVendorRoutes,
  eventOrganizerRoutes,
  normalizeRequiredPermission,
};

// export default routes;
